import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Plan } from "@shared/schema";
import { deriveCoverageDates } from "@shared/utils/planCoverageDates";
import { onePlanYearLinkPerPlan } from "@shared/utils/planYearLinks";

/**
 * Hook to fetch and merge client plans
 * 
 * Fetches all plans and client-plan associations, then merges them
 * to return plans with client-specific effective/end dates.
 * 
 * @param clientId - Optional client ID. If not provided, hook will be disabled.
 * @returns Object with merged plans, loading state, and error state
 */
export function useClientPlans(clientId?: number | null) {
  // Fetch all plans
  const { data: allPlans = [], isLoading: isLoadingAll, error: errorAll } = useQuery<any[]>({
    queryKey: ["/api/plans"],
    queryFn: async () => {
      const res = await fetch("/api/plans");
      if (!res.ok) return [] as any[];
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
  });

  // Fetch client-plan associations
  const { data: clientPlans = [], isLoading: isLoadingClient, error: errorClient } = useQuery<any[]>({
    queryKey: ["/api/client-plans", clientId],
    enabled: Boolean(clientId),
    queryFn: async () => {
      const res = await fetch(`/api/client-plans?clientId=${clientId}`);
      if (!res.ok) return [] as any[];
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
  });

  // Merge plans: combine plan data with client-specific dates.
  // Only include active client-plan associations (exclude quoted/inactive plans).
  const planYears = useMemo(() => {
    const clientPlansArray = Array.isArray(clientPlans) ? (clientPlans as any[]) : [];
    return clientPlansArray
      .filter((cp: any) => {
        // Exclude non-active client-plan associations (e.g. quoted plans)
        const cpStatus = ((cp as any).status || "active").toLowerCase();
        const cpIsActive = (cp as any).isActive ?? true;
        return cpStatus === "active" && cpIsActive !== false;
      })
      .map((cp: any) => {
        // Try to find plan in allPlans first
        const fromAll = (allPlans as any[]).find((p) => p.id === cp.planId);
        // Fallback to plan data that might be joined in clientPlans response
        const fromJoin = (cp as any)?.plan;
        const planObj = fromAll || fromJoin;

        // Filter out orphaned client-plan rows lacking a valid plan record
        if (!planObj || !(planObj as any)?.id || !(planObj as any)?.name) {
          return null;
        }

        const mergedEffectiveDate = cp.effectiveDate ?? (planObj as any).effectiveDate;
        const mergedEndDate = cp.endDate ?? (planObj as any).endDate;
        // Available Start/End Date gate when an employee can actually enroll
        // (see client/src/pages/employee/Benefits.tsx) - they must follow
        // THIS client's own effective date, not the plan's catalog one,
        // same as effectiveDate/endDate above. Derived, not stored: a
        // client's own row on client_plans has no availableStartDate/
        // availableEndDate columns because these were never meant to be
        // independently editable.
        const derived = deriveCoverageDates(mergedEffectiveDate);

        return {
          ...planObj,
          clientPlanId: cp.id,
          // Client-specific carrier group/policy number (client_plans link),
          // NOT the shared library plan's planCode.
          groupNumber: (cp as any).groupNumber ?? null,
          // Set when this plan year was renewed: the link it was renewed into
          // (which may be on a different plan).
          renewedToClientPlanId: (cp as any).renewedToClientPlanId ?? null,
          effectiveDate: mergedEffectiveDate,
          endDate: mergedEndDate,
          renewalDate: (cp as any).renewalDate ?? (planObj as any).renewalDate,
          availableStartDate: derived.availableStartDate ?? (planObj as any).availableStartDate,
          availableEndDate: mergedEndDate ?? (planObj as any).availableEndDate,
        } as any;
      })
      .filter(Boolean) as Plan[];
  }, [allPlans, clientPlans]);

  // A renewed plan has a link per plan year (current + upcoming). Pickers,
  // enrollment and counts want each plan ONCE, through the plan year in force
  // today; only the plan list shows every plan year (planYears).
  const mergedPlans = useMemo(
    () => onePlanYearLinkPerPlan(planYears as any[], (p) => Number(p.id)) as Plan[],
    [planYears],
  );

  return {
    plans: mergedPlans,
    planYears,
    allPlans: allPlans, // Expose all plans for lookups by planId
    isLoading: isLoadingAll || isLoadingClient,
    error: errorAll || errorClient,
  };
}

